import { FC, useEffect, useState } from "react"
import { Ticket } from "../../../../types/slices/ticketType"
import {
    Drawer, Grid, IconButton, Typography,
    Table, TableBody, TableCell, TableContainer,
    TableRow, Paper, Divider,
    Button,
    TableHead,
    TextField,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CustomTable from "../../../../components/common/CustomTable/CustomTable"
import { moneyCL } from "../../../../toolbox/helpers/money-helpers"
import { formatDate } from "../../../../toolbox/helpers/formatDateHelper"
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ModalConfirm } from "../../../../components/common/ModalConfirm/ModalConfirm"
import BlockIcon from "@mui/icons-material/Block";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import '../../../../assets/styles/facturacionv3-app.css';
import { withStyles } from '@mui/styles';
import { useTicketStore } from "../../../../hooks/useTicketStore"

interface Props {
    open: boolean
    onClose: () => void
    onDeleteConfirm: () => Promise<void>
    ticket: Ticket
    loading: boolean
    disabledEdit?: boolean
    getTicketApi?: () => Promise<void>
}

const StyledTableCell = ({ children, sx = {}, ...props }) => (
    <TableCell sx={{ borderBottom: '1px solid #ddd', fontSize: 14, padding: '8px', ...sx }} {...props}>
        {children}
    </TableCell>
)

const HeaderCell = ({ children }) => (
    <StyledTableCell sx={{ background: '#69ABC3', color: '#fff', fontWeight: 'bold', textAlign: 'left' }}>
        {children}
    </StyledTableCell>
)

const StyledTableCell2 = withStyles((theme) => ({
    head: {
        color: theme.palette.common.black,
        backgroundColor: '#69ABC3'
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const columns = [
    { type: 'text', field: 'code_system', label: 'Código', align: 'center' },
    { type: 'text', field: 'product', label: 'Producto', align: 'center' },
    { type: 'text', field: 'unitmeasure_abr', label: 'Und. Med.', align: 'center' },
    { type: 'text', field: 'amount', label: 'Cantidad', align: 'center' },
    { type: 'text', field: 'price', label: 'Precio', align: 'right', format: (row) => moneyCL(row.price) },
    { type: 'text', field: 'net_subtotal', label: 'Descuento ($)', align: 'right', format: (row) => moneyCL((row.price * row.amount) - row.net_subtotal) },
    { type: 'text', field: 'net_subtotal', label: 'Total', align: 'right', format: (row) => moneyCL(row.net_subtotal) },
]

export const SaleDetailDrawer: FC<Props> = ({ open, onClose, ticket, onDeleteConfirm, loading, disabledEdit, getTicketApi }) => {

    const { partialCancelation } = useTicketStore()

    const [dataTable, setDataTable] = useState<any[]>([])
    const [modifiedProducts, setModifiedProducts] = useState<any[]>([])

    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
    const [partialCancellation, setPartialCancellation] = useState<boolean>(false)

    useEffect(() => {
        if (ticket?.details.length > 0) {
            setDataTable(ticket?.details || [])
        }
    }, [ticket])

    const onSeeDocument = () => {
        if (!ticket.url_invoice) return

        window.open(ticket.url_invoice, "_blank")
    }

    const handleSave = async () => {
        const resp = await partialCancelation(ticket?.id, {
            changes: modifiedProducts
        })
        onClose();
        setPartialCancellation(false);
        getTicketApi && getTicketApi();
    }

    const handleCancel = () => {
        setDataTable(ticket?.details || [])
        setPartialCancellation(false)
    }

    const handleProductChange = (id: number, value: string) => {
        const updatedTable = dataTable.map((product) => {
            if (product.id === id) {
                return {
                    ...product,
                    amount: value,
                    net_subtotal: product.price * Number(value)
                }
            }
            return product
        })
        setDataTable(updatedTable)

        const modified = {
            id,
            idproduct: updatedTable.find(p => p.id === id)?.idproduct || 0,
            amount: updatedTable.find(p => p.id === id)?.amount || 0,
            net_subtotal: Math.ceil(updatedTable.find(p => p.id === id)?.net_subtotal || 0),
        }

        setModifiedProducts(prevState => {
            const existingIndex = prevState.findIndex(item => item.id === id)
            if (existingIndex >= 0) {
                const updatedState = [...prevState]
                updatedState[existingIndex] = modified
                return updatedState
            } else {
                return [...prevState, modified]
            }
        })
    }

    return (
        <Drawer anchor="right" open={open} onClose={() => { onClose(); setPartialCancellation(false) }} sx={{ "& .MuiDrawer-paper": { width: 900, padding: 3 } }}>
            <IconButton
                onClick={() => {
                    onClose();
                    setPartialCancellation(false)
                }}
                sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#333"
                }}
                tabIndex={-1}
            >
                <CloseIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold" color="#333">
                Detalle de la venta
            </Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />

            <Paper elevation={0} sx={{ padding: 3, background: "#f5f5f5" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <HeaderCell>N° Ticket</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.nro_ticket}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Fecha</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.date ? formatDate(ticket?.date): ''}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Documento</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.type_document}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Forma de pago</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.way_to_pay}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Estado de Pago</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.payment_status_name}</StyledTableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <HeaderCell>Cliente</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.name_customer}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>RUT</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.rut_customer}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Giro</HeaderCell>
                                        <StyledTableCell align="left">{ticket?.giro_customer}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Dirección</HeaderCell>
                                        <StyledTableCell align="left">{`${ticket?.client_address}, ${ticket?.district_customer}, ${ticket?.province_customer}`}</StyledTableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>

                <Grid item xs={12} mt={2} sx={{ textAlign: 'right' }}>
                    {!partialCancellation ? (<>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#516E80",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 12,
                                padding: "6px 12px",
                                width: "150px",
                                gap: "6px",
                                "&:hover": {
                                    backgroundColor: "#415766",
                                },
                            }}
                            onClick={onSeeDocument}
                        >
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                            Ver documento
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#D32F2F",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 12,
                                padding: "6px 12px",
                                gap: "6px",
                                width: "150px",
                                marginLeft: "5px",
                                "&:hover": {
                                    backgroundColor: "#B71C1C",
                                },
                            }}
                            onClick={() => setShowModalConfirm(true)}
                            disabled={disabledEdit}
                        >
                            <BlockIcon sx={{ fontSize: 18 }} />
                            Anular
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#FFA000",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 12,
                                padding: "6px 12px",
                                gap: "6px",
                                width: "150px",
                                marginLeft: "5px",
                                "&:hover": {
                                    backgroundColor: "#FF8F00",
                                },
                            }}
                            onClick={() => setPartialCancellation(true)}
                            disabled={disabledEdit}
                        >
                            <BlockIcon sx={{ fontSize: 18 }} />
                            Anulación Parcial
                        </Button>
                    </>) : (<>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#388E3C",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 12,
                                padding: "6px 12px",
                                gap: "6px",
                                width: "150px",
                                marginLeft: "5px",
                                "&:hover": {
                                    backgroundColor: "#2E7D32",
                                },
                            }}
                            onClick={handleSave}
                        >
                            <SaveIcon sx={{ fontSize: 18 }} />
                            Guardar
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#757575",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 12,
                                padding: "6px 12px",
                                gap: "6px",
                                width: "150px",
                                marginLeft: "5px",
                                "&:hover": {
                                    backgroundColor: "#616161",
                                },
                            }}
                            onClick={handleCancel}
                        >
                            <CancelIcon sx={{ fontSize: 18 }} />
                            Cancelar
                        </Button>
                    </>)}
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {!partialCancellation ?
                            <CustomTable
                                columns={columns}
                                rows={ticket?.details || []}
                                withoutPagination
                                onRowClick={() => null}
                                dense
                            />
                            :
                            <TableContainer sx={{ marginTop: "35px" }}>
                                <Table stickyHeader size="small">
                                    <TableHead className="table-header" >
                                        <TableRow sx={{ height: '32px' }}>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Código
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Producto
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Und. Med.
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Cantidad
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Precio
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Descuento
                                            </StyledTableCell2>
                                            <StyledTableCell2 className="table-header-text" align="center">
                                                Total
                                            </StyledTableCell2>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dataTable?.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell align="center">{row.code_system}</TableCell>
                                                <TableCell align="center">{row.product}</TableCell>
                                                <TableCell align="center">{row.unitmeasure_abr}</TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        id={"quantity_" + row.idproduct}
                                                        type="text"
                                                        name={"quantity_" + row.idproduct}
                                                        size="small"
                                                        value={row?.amount}
                                                        onChange={(e) => {
                                                            const { value } = e.target

                                                            const isValid =
                                                                row?.idunitmeasure_product == 1
                                                                    ? /^\d+$/.test(value)
                                                                    : /^\d*\.?\d{0,2}$/.test(value)

                                                            if (isValid || value === "") {
                                                                handleProductChange(row?.id, value)
                                                            }
                                                        }}
                                                        inputProps={{
                                                            min: 0,
                                                            inputMode: "decimal",
                                                            pattern: "[0-9]*"
                                                        }}
                                                        sx={{
                                                            width: '80px',
                                                            "& .MuiInputBase-root": { fontSize: '14px' },
                                                        }}
                                                        disabled={!partialCancellation}
                                                        variant="standard"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{moneyCL(row.price)}</TableCell>
                                                <TableCell align="right">$0</TableCell>
                                                <TableCell align="right">{moneyCL(row.net_subtotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        }
                    </Grid>

                    <Grid item xs={12} sm={5} sx={{ marginLeft: "auto" }}>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <HeaderCell>Subtotal</HeaderCell>
                                        <StyledTableCell align="right">{moneyCL(ticket?.sub_total || 0)}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Descuento</HeaderCell>
                                        <StyledTableCell align="right">{moneyCL(ticket?.total_discount)}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>Monto Neto</HeaderCell>
                                        <StyledTableCell align="right">{moneyCL(ticket?.net_total)}</StyledTableCell>
                                    </TableRow>
                                    {ticket?.idtype_document === 2 && (
                                        <TableRow>
                                            <HeaderCell>Monto Exento</HeaderCell>
                                            <StyledTableCell align="right">{moneyCL(0)}</StyledTableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <HeaderCell>19% IVA</HeaderCell>
                                        <StyledTableCell align="right">{moneyCL(ticket?.IVA)}</StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                        <HeaderCell>TOTAL</HeaderCell>
                                        <StyledTableCell align="right" sx={{ fontWeight: "bold", fontSize: 16 }}>
                                            {moneyCL(ticket?.gross_total)}
                                        </StyledTableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Paper>
            {
                showModalConfirm && (
                    <ModalConfirm
                        open={showModalConfirm}
                        closeModal={() => { setShowModalConfirm(false) }}
                        onConfirm={() => {onDeleteConfirm(); setShowModalConfirm(false); onClose()}}
                        status={loading}
                        title="¿Está seguro de anular este documento?"
                        text="Una vez anulado no podrá ser recuperado."
                    />
                )
            }
        </Drawer>
    )
}
